import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { getReinoById, getPdfUrl, PortalReino } from '../../lib/portalData';
import { PDFReader } from './PDFReader';
interface PortalViewsProps {
  view: 'library' | 'reino' | 'reader';
  userAccess: any | null;
  availableRealms: PortalReino[];
  selectedReinoId: string | null;
  selectedDocumentKey: string | null;
  onSelectReino: (id: string) => void;
  onSelectDocument: (key: string) => void;
  onBackToLibrary: () => void;
  onBackToReino: () => void;
}
export function PortalViews({
  view,
  userAccess,
  availableRealms,
  selectedReinoId,
  selectedDocumentKey,
  onSelectReino,
  onSelectDocument,
  onBackToLibrary,
  onBackToReino
}: PortalViewsProps) {
  const canAccess = (reinoId: string) => availableRealms.some(r => r.id === reinoId);
  const reinoForView = view !== 'library' && selectedReinoId ? getReinoById(selectedReinoId) : null;
  const readerDoc = view === 'reader' && reinoForView && selectedDocumentKey ? reinoForView.documents.find(d => d.productKey === selectedDocumentKey) || null : null;
  return <>
      {view === 'library' && <ReinosLibrary availableRealms={availableRealms} onSelectReino={onSelectReino} data-id="element-537" />}

      {view === 'reino' && reinoForView && <>
          {canAccess(reinoForView.id) ? <ReinoVista reino={reinoForView} onSelectDocument={onSelectDocument} onBack={onBackToLibrary} data-id="element-538" /> : <NoAccess onBack={onBackToLibrary} data-id="element-539" />}
        </>}

      {view === 'reader' && reinoForView && readerDoc && canAccess(reinoForView.id) && <PDFReader pdfUrl={getPdfUrl(readerDoc.pdfPath)} title={readerDoc.title} onClose={onBackToReino} data-id="element-540" />}
    </>;
}
function NoAccess({
  onBack
}: {
  onBack: () => void;
}) {
  return <div className="max-w-md mx-auto text-center bg-cosmic-black/80 border border-red-500/20 rounded-2xl p-8" data-id="element-541">
      <p className="text-red-400 font-serif italic text-lg mb-6" data-id="element-542">
        No tienes acceso a este Reino.
      </p>
      <button onClick={onBack} className="px-6 py-2 rounded-lg border border-gold-primary/30 text-gold-light text-sm tracking-widest uppercase hover:bg-gold-primary/10 transition-colors" data-id="element-543">
        Volver a los Reinos
      </button>
    </div>;
}
function ReinosLibrary({
  availableRealms,
  onSelectReino
}: {
  availableRealms: PortalReino[];
  onSelectReino: (id: string) => void;
}) {
  const isSingle = availableRealms.length === 1;
  return <div className="w-full max-w-6xl mx-auto" data-id="element-544">
      <div className="text-center mb-12" data-id="element-545">
        <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-546">
          {isSingle ? 'Tu Reino' : 'Elige tu Reino'}
        </h2>
        <p className="text-gold-light/80 font-serif italic text-lg" data-id="element-547">
          {isSingle ? 'Tu archivo privado de lectura te espera.' : 'Cada Reino contiene tus documentos privados de lectura.'}
        </p>
      </div>

      <div className={isSingle ? 'flex justify-center' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'} data-id="element-548">
        {availableRealms.map((reino, i) => <motion.div key={reino.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.05
      }} onClick={() => onSelectReino(reino.id)} className={`group relative rounded-2xl overflow-hidden cursor-pointer aspect-[3/4] border border-gold-primary/20 hover:border-gold-primary/60 transition-colors ${isSingle ? 'w-full max-w-sm' : ''}`} data-id="element-549">
            <img src={reino.imageUrl} alt={reino.nombre} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-550" />
            <div className="absolute inset-0 bg-gradient-to-t from-cosmic-black via-cosmic-black/40 to-transparent" data-id="element-551" />

            <div className="absolute inset-0 p-6 flex flex-col justify-end items-center text-center" data-id="element-552">
              <span className="text-4xl mb-2 opacity-80 group-hover:opacity-100 transition-opacity" style={{
            color: reino.color
          }} data-id="element-553">
                {reino.symbol}
              </span>
              <h3 className="text-2xl font-serif font-bold text-white mb-4 tracking-wider" data-id="element-554">
                {reino.nombre}
              </h3>
              <button className="px-6 py-2 rounded-full border border-gold-light/30 text-gold-light text-xs tracking-widest uppercase group-hover:bg-gold-light/10 transition-colors" data-id="element-555">
                Entrar
              </button>
            </div>
          </motion.div>)}
      </div>
    </div>;
}
function ReinoVista({
  reino,
  onSelectDocument,
  onBack
}: {
  reino: PortalReino;
  onSelectDocument: (key: string) => void;
  onBack: () => void;
}) {
  return <div id="reino-documents" className="relative w-full max-w-5xl mx-auto rounded-3xl overflow-hidden px-5 md:px-10 py-10 md:py-14" data-id="element-556">
      {/* Atmospheric per-sign background */}
      <div className="absolute inset-0 -z-10 pointer-events-none" data-id="element-557">
        <img src={reino.imageUrl} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" style={{
        opacity: 0.55
      }} loading="lazy" data-id="element-558" />
        {/* Sign color tint */}
        <div className="absolute inset-0" style={{
        background: `radial-gradient(ellipse at 50% 20%, ${reino.color}40 0%, transparent 65%)`
      }} data-id="element-559" />
        {/* Dark legibility overlay */}
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(180deg, rgba(5,5,8,0.75) 0%, rgba(5,5,8,0.55) 40%, rgba(5,5,8,0.85) 100%)'
      }} data-id="element-560" />
        {/* Soft inner border glow */}
        <div className="absolute inset-0 rounded-3xl" style={{
        border: `1px solid ${reino.color}40`,
        boxShadow: `inset 0 0 60px ${reino.color}22`
      }} data-id="element-561" />
      </div>

      <button onClick={onBack} className="mb-8 text-gold-light/70 hover:text-gold-light transition-colors text-sm tracking-widest uppercase flex items-center gap-2" data-id="element-562">
        ← Volver a los Reinos
      </button>

      <div className="text-center mb-12 md:mb-16" data-id="element-563">
        <span className="text-6xl mb-4 block" style={{
        color: reino.color,
        textShadow: `0 0 24px ${reino.color}80`
      }} data-id="element-564">
          {reino.symbol}
        </span>
        <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4" style={{
        textShadow: '0 2px 20px rgba(0,0,0,0.85)'
      }} data-id="element-565">
          Reino de {reino.nombre}
        </h2>
        <p className="text-gold-light/85 font-serif italic text-lg max-w-2xl mx-auto" style={{
        textShadow: '0 2px 14px rgba(0,0,0,0.85)'
      }} data-id="element-566">
          Tus documentos privados de lectura están disponibles aquí.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6" data-id="element-567">
        {reino.documents.map((doc, i) => <motion.button key={doc.productKey} type="button" onClick={() => onSelectDocument(doc.productKey)} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.08
      }} whileHover={{
        y: -2
      }} whileTap={{
        scale: 0.985
      }} className="relative rounded-2xl p-[1px] overflow-hidden group text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-light/60" style={{
        background: `linear-gradient(145deg, ${reino.color}55 0%, transparent 100%)`
      }} data-id="element-568">
            <div className="relative h-full bg-cosmic-black/90 backdrop-blur-sm rounded-2xl p-6 md:p-8 flex flex-col gap-3 min-h-[200px] transition-colors duration-300 group-hover:bg-cosmic-black/95" data-id="element-569">
              <div className="flex items-start justify-between gap-4" data-id="element-570">
                <h3 className="text-lg md:text-xl font-serif font-bold text-white tracking-wide uppercase leading-tight" data-id="element-571">
                  {doc.title}
                </h3>
                <span className="flex-shrink-0 mt-1 text-gold-light/70 group-hover:text-gold-light transition-colors text-xl leading-none" aria-hidden="true" data-id="element-572">
                  →
                </span>
              </div>

              <p className="text-white/60 font-serif italic text-sm md:text-base leading-relaxed" data-id="element-573">
                {doc.description}
              </p>

              {/* Prominent CTA pill — clearly communicates the card is tappable */}
              <span className="mt-3 inline-flex items-center justify-center gap-2 self-start px-5 py-2.5 rounded-full text-xs md:text-sm font-bold tracking-[0.2em] uppercase shadow-lg transition-all duration-300 group-hover:scale-[1.03]" style={{
            background: 'linear-gradient(135deg, #C9A84C 0%, #E8D48B 100%)',
            color: '#050508',
            boxShadow: '0 0 24px rgba(232,212,139,0.45)',
            fontFamily: 'Playfair Display, serif'
          }} data-id="element-574">
                Abrir lectura
                <span aria-hidden="true" className="text-base leading-none" data-id="element-575">
                  →
                </span>
              </span>
            </div>
          </motion.button>)}
      </div>
    </div>;
}